import React from 'react';
import { Link } from 'react-router-dom';

const CAPABILITIES = [
  {
    id: 1,
    icon: '🔊',
    tag: 'ACOUSTIC',
    title: 'Acoustic Diagnostics',
    description: 'Spectral, temporal, and harmonic feature extraction for respiratory sound analysis and early pulmonary screening.',
    route: '/acoustic-analysis',
  },
  {
    id: 2,
    icon: '🩻',
    tag: 'RADIOLOGY',
    title: 'Radiology Diagnostics',
    description: 'Chest X-ray and CT analysis with imaging-driven disease insights and spatial attribution.',
    route: '/image-analysis',
  },
  {
    id: 3,
    icon: '🔬',
    tag: 'PATHOLOGY',
    title: 'Pathology Diagnostics',
    description: 'Digital pathology, whole-slide imaging, and biological signal interpretation for disease detection.',
    route: '/pathology-analysis',
  },
  {
    id: 4,
    icon: '📋',
    tag: 'CLINICAL',
    title: 'Clinical Diagnostics',
    description: 'Clinical history, symptoms, vitals, and EHR-driven reasoning for comprehensive diagnostic insights.',
    route: '/clinical-analysis',
  },
  {
    id: 5,
    icon: '🧬',
    tag: 'MOLECULAR',
    title: 'Molecular Diagnostics',
    description: 'PCR, GeneXpert, molecular biomarkers, mutation profiling, and companion diagnostics for precision disease characterization.',
    route: '/molecular-analysis',
  },
  {
    id: 6,
    icon: '🔗',
    tag: 'MULTIMODAL',
    title: 'Multimodal Diagnostics',
    description: 'Fusion of acoustics, radiology, pathology, clinical, and molecular signals using advanced AI with attention mechanisms.',
    route: '/interactive-demo',
  },
];

const DISEASES = ['Tuberculosis', 'COPD', 'ILD', 'Pneumonia', 'Lung Cancer'];

export default function Platform() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-tealLight/30 to-paper text-ink antialiased selection:bg-teal/20 selection:text-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-12">

        {/* HERO SECTION */}
        <div className="text-center mb-10 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-tealLight border border-teal/20 text-teal text-xs font-semibold mb-5 uppercase tracking-wider">
            Multimodal Diagnostics · Acoustic · Radiology · Pathology · Clinical · Molecular
          </div>

          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-sans font-bold leading-tight tracking-tight text-center text-teal">
            Clinical Diagnostic Platform
          </h1>

          <p className="mt-5 text-lg sm:text-xl text-inkSoft max-w-3xl mx-auto leading-relaxed text-center">
            The diagnostic bridge between clinician and diagnosis. Integrating acoustics,
            radiology, pathology, clinical data, molecular diagnostics, and multimodal AI into a unified platform.
          </p>

          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {DISEASES.map((disease) => (
              <span
                key={disease}
                className="px-3 py-1.5 bg-white border border-rule rounded-lg text-xs font-medium text-ink shadow-sm hover:border-teal/40 transition-all"
              >
                {disease}
              </span>
            ))}
          </div>
        </div>

        {/* CAPABILITY CARDS — Reduced Size */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-20">
          {CAPABILITIES.map((cap) => (
            <div
              key={cap.id}
              className="group relative bg-white border border-rule rounded-lg p-4 shadow-[3px_3px_0_0_rgba(20,184,166,0.15)] hover:shadow-[5px_5px_0_0_rgba(20,184,166,0.25)] hover:-translate-y-0.5 hover:-translate-x-0.5 transition-all duration-200 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 bg-tealLight border border-teal/20 rounded-lg flex items-center justify-center text-xl">
                    {cap.icon}
                  </div>
                  <span className="px-2 py-0.5 rounded-md text-[9px] font-bold tracking-wider uppercase border bg-tealLight text-teal border-teal/30">
                    {cap.tag}
                  </span>
                </div>

                <h3 className="text-sm sm:text-base font-bold text-ink group-hover:text-teal transition-colors mb-1.5">
                  {cap.title}
                </h3>

                <p className="text-inkSoft text-xs leading-relaxed">
                  {cap.description}
                </p>
              </div>

              <div className="mt-4 pt-3 border-t border-rule">
                <div className="flex items-center gap-1.5 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  <span className="text-emerald-700 text-[10px] font-semibold">
                    Live Demo Available
                  </span>
                </div>

                <Link
                  to={cap.route}
                  className="flex items-center justify-center gap-2 w-full rounded-lg bg-tealLight text-teal font-bold text-sm px-3 py-2.5 shadow-sm border border-teal/40 transition-all duration-200 hover:bg-teal hover:text-white hover:border-teal hover:shadow-md"
                >
                  <span>Virtual Lab</span>
                  <svg
                    className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-1"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>

                <p className="text-center text-[10px] text-teal/60 mt-1.5">
                  Interactive Testing Environment
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* PRECISION MEDICINE — Full-Width Row */}
        <div className="mb-20">
          <div className="bg-gradient-to-r from-tealLight/50 to-tealLight/30 border border-teal/30 rounded-xl p-6 sm:p-8 shadow-md hover:border-teal/50 transition-all">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 bg-teal/10 border border-teal/30 rounded-2xl flex items-center justify-center text-3xl">
                  💊
                </div>
              </div>
              <div className="flex-1 text-center sm:text-left">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <span className="px-2.5 py-0.5 rounded-md text-[10px] font-bold tracking-wider uppercase border bg-teal/20 text-teal border-teal/40">
                    PRECISION MEDICINE
                  </span>
                  <span className="text-xs text-teal/60 font-medium">Multi-Omics Integration</span>
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-ink">Precision Medicine & Therapeutic Insights</h3>
                <p className="text-inkSoft text-sm leading-relaxed mt-1 max-w-3xl">
                  Integrates multimodal diagnostics, molecular biomarkers, and multi-omics to enable disease subtyping,
                  personalized risk assessment, treatment selection, companion diagnostics, and precision therapeutics.
                </p>
              </div>
              <div className="flex-shrink-0">
                <Link
                  to="/solutions/precision-medicine"
                  className="flex items-center gap-2 rounded-lg bg-teal text-white font-bold text-sm px-5 py-2.5 shadow-md border border-teal/40 transition-all duration-200 hover:bg-tealDark hover:shadow-lg"
                >
                  <span>Explore</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* ARCHITECTURE PIPELINE — Updated to align with patent terminology */}
        <div className="max-w-5xl mx-auto mb-16">
          <h2 className="text-2xl sm:text-3xl font-bold text-center text-ink mb-6 tracking-tight">
            Platform Architecture Flow
          </h2>

          <div className="bg-white border border-rule rounded-xl p-5 shadow-md">
            <div className="flex flex-wrap justify-center items-center gap-2">
              {['Acoustic', 'Radiology', 'Pathology', 'Clinical', 'Molecular'].map((layer, idx) => (
                <React.Fragment key={layer}>
                  <div className="px-3 py-1.5 bg-white border border-rule text-xs font-semibold rounded-lg text-ink shadow-sm">
                    {layer}
                  </div>
                  {idx < 4 && <span className="text-rule text-base font-light">→</span>}
                </React.Fragment>
              ))}
            </div>

            <div className="flex flex-wrap justify-center items-center gap-2 mt-4 pt-4 border-t border-rule">
              <div className="px-3 py-1.5 bg-tealLight border border-teal/30 rounded-lg shadow-sm font-semibold text-xs text-teal">
                Semantic Projection
              </div>
              <span className="text-rule text-base font-light">→</span>
              <div className="px-3 py-1.5 bg-tealLight border border-teal/30 rounded-lg shadow-sm font-semibold text-xs text-teal">
                Clinical Intelligence Workspace
              </div>
              <span className="text-rule text-base font-light">→</span>
              <div className="px-3 py-1.5 bg-tealLight border border-teal/30 rounded-lg shadow-sm font-semibold text-xs text-teal">
                CTF Engine
              </div>
              <span className="text-rule text-base font-light">→</span>
              <div className="px-3 py-1.5 bg-tealLight border border-teal/30 rounded-lg shadow-sm font-semibold text-xs text-teal">
                Clinical Decision Support
              </div>
              <span className="text-rule text-base font-light">→</span>
              <div className="px-3 py-1.5 bg-teal text-white rounded-lg shadow-md font-bold text-xs">
                Precision Medicine
              </div>
            </div>
          </div>
        </div>

        {/* DEEP TECH / CTF SECTION */}
        <div className="bg-white border border-rule rounded-xl p-6 sm:p-8 shadow-md mb-16">
          <h3 className="text-xl sm:text-2xl font-bold mb-3 text-ink tracking-tight">
            🎯 Concept Tuning & Fusion (CTF)
          </h3>
          <p className="text-inkSoft leading-relaxed max-w-3xl text-sm sm:text-base mb-6">
            CTF is the semantic reasoning engine that aligns acoustics, radiology, pathology,
            clinical data, molecular diagnostics, and multi-omics into a shared diagnostic intelligence space.
            It enables multimodal reasoning while preserving clinically relevant patterns across heterogeneous healthcare data.
          </p>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              { icon: '🔗', title: 'Semantic Alignment', desc: 'Connects heterogeneous clinical signals into a common diagnostic model.' },
              { icon: '💡', title: 'Clinical Reasoning', desc: 'Supports multimodal decision-making across incomplete datasets.' },
              { icon: '🔍', title: 'Explainable AI', desc: 'Delivers concept-linked attribution and uncertainty-aware outputs.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-tealLight/40 border border-teal/20 p-4 rounded-lg hover:border-teal/40 transition-all">
                <div className="text-xl mb-1.5">{item.icon}</div>
                <h4 className="font-bold mb-1 text-teal text-sm">
                  {item.title}
                </h4>
                <p className="text-inkSoft text-xs leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* BOTTOM CALL TO ACTION */}
        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-xl sm:text-2xl font-bold text-ink tracking-tight mb-2">
            Experience the Future of Diagnostics
          </h2>
          <p className="text-inkSoft text-sm mb-6 leading-relaxed">
            Explore acoustic screening, radiology analysis, pathology intelligence, molecular diagnostics,
            and CTF-powered disease reasoning.
          </p>
          <Link
            to="/virtual-lab"
            className="inline-flex items-center justify-center bg-tealLight text-teal font-bold px-6 py-3 rounded-lg shadow-md border border-teal/40 transition-all duration-200 transform hover:scale-105 hover:bg-teal hover:text-white hover:border-teal hover:shadow-lg"
          >
            Enter Virtual Lab →
          </Link>
        </div>

      </div>
    </div>
  );
}
